import React from 'react';
import { CalendarDays, Check, ClipboardList, FileSignature, Pill, ScanLine, Smile } from 'lucide-react';

// Pantallas simuladas del sistema para la landing. Son ilustrativas: datos de ejemplo, no pacientes reales.

const Frame = ({ icon: Icon, title, children }) => (
  <div className="lp-mock">
    <div className="lp-mock-bar">
      <span className="lp-mock-dots"><i /><i /><i /></span>
      <span className="lp-mock-url">medix.cl/app</span>
    </div>
    <div className="lp-mock-body">
      <div className="lp-mock-title"><Icon size={16} /> {title}</div>
      {children}
    </div>
  </div>
);

const AGENDA = [
  { col: 0, start: 0, len: 2, who: 'Control', tone: 'sky' },
  { col: 0, start: 3, len: 1, who: 'Limpieza', tone: 'mint' },
  { col: 0, start: 5, len: 2, who: 'Endodoncia', tone: 'violet' },
  { col: 1, start: 1, len: 1, who: 'Evaluación', tone: 'amber' },
  { col: 1, start: 2, len: 2, who: 'Ortodoncia', tone: 'sky' },
  { col: 1, start: 6, len: 1, who: 'Control', tone: 'mint' },
  { col: 2, start: 0, len: 1, who: 'Urgencia', tone: 'rose' },
  { col: 2, start: 2, len: 3, who: 'Implante', tone: 'violet' },
  { col: 2, start: 6, len: 1, who: 'Evaluación', tone: 'amber' },
];
const HOURS = ['09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00'];

const AgendaMock = () => (
  <Frame icon={CalendarDays} title="Agenda · Hoy">
    <div className="lp-agenda">
      <div className="lp-agenda-hours">{HOURS.map((h) => <span key={h}>{h}</span>)}</div>
      {['Box 1', 'Box 2', 'Box 3'].map((box, col) => (
        <div key={box} className="lp-agenda-col">
          <b>{box}</b>
          <div className="lp-agenda-slots">
            {AGENDA.filter((a) => a.col === col).map((a) => (
              <span
                key={a.start}
                className={`lp-appt lp-tone-${a.tone}`}
                style={{ top: `${(a.start / 7) * 100}%`, height: `calc(${(a.len / 7) * 100}% - 4px)` }}
              >
                {a.who}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  </Frame>
);

const UPPER = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28];
const FINDINGS = { 16: 'caries', 14: 'obturado', 11: 'caries', 25: 'obturado', 26: 'corona', 27: 'caries' };

const OdontogramMock = () => (
  <Frame icon={Smile} title="Odontograma · Arcada superior">
    <div className="lp-odonto">
      {UPPER.map((n) => (
        <figure key={n} className={FINDINGS[n] ? `lp-tooth lp-find-${FINDINGS[n]}` : 'lp-tooth'}>
          <img src={`/odontogram/teeth/${n}.png`} alt="" loading="lazy" />
          <figcaption>{n}</figcaption>
        </figure>
      ))}
    </div>
    <div className="lp-legend">
      <span className="lp-find-caries">Caries</span>
      <span className="lp-find-obturado">Obturación</span>
      <span className="lp-find-corona">Corona</span>
    </div>
  </Frame>
);

const FichaMock = () => (
  <Frame icon={ClipboardList} title="Ficha clínica">
    <div className="lp-ficha-head">
      <span className="lp-avatar">CR</span>
      <div>
        <strong>Paciente de ejemplo</strong>
        <small>34 años · Previsión: Fonasa</small>
      </div>
    </div>
    <div className="lp-ficha-tags">
      <span>Alergia: penicilina</span><span>Hipertensión</span>
    </div>
    <ul className="lp-timeline">
      <li><b>Hoy</b> Control post endodoncia · pieza 16</li>
      <li><b>12 ago</b> Endodoncia · pieza 16</li>
      <li><b>30 jul</b> Evaluación inicial y radiografía</li>
    </ul>
  </Frame>
);

const ConsentMock = () => (
  <Frame icon={FileSignature} title="Consentimiento informado">
    <div className="lp-doc">
      <strong>Consentimiento para tratamiento de endodoncia</strong>
      <i /><i /><i className="lp-short" />
      <div className="lp-sign">
        <svg viewBox="0 0 200 60" aria-hidden="true">
          <path d="M8 44 C 30 10, 42 58, 62 30 S 92 8, 104 36 S 138 54, 150 24 S 176 20, 192 34" />
        </svg>
        <span>Firma del paciente</span>
      </div>
      <p className="lp-signed"><Check size={14} /> Firmado y guardado en la ficha</p>
    </div>
  </Frame>
);

const RecetaMock = () => (
  <Frame icon={Pill} title="Receta">
    <div className="lp-doc">
      <strong>Receta médica</strong>
      <ul className="lp-rx-list">
        <li><b>Ibuprofeno 400 mg</b><span>1 comprimido cada 8 horas por 3 días</span></li>
        <li><b>Amoxicilina 500 mg</b><span>1 cápsula cada 8 horas por 7 días</span></li>
      </ul>
      <div className="lp-chips-mini"><span>Desde catálogo</span><span>Lista para imprimir</span></div>
    </div>
  </Frame>
);

const RxMock = () => (
  <Frame icon={ScanLine} title="Radiografías · Comparar controles">
    <div className="lp-rx">
      {['30 jul', 'Hoy'].map((d) => (
        <div key={d} className="lp-rx-img">
          <div className="lp-rx-film"><i /><i /><i /></div>
          <span>{d}</span>
        </div>
      ))}
    </div>
    <p className="lp-signed"><Check size={14} /> Informe radiológico generado</p>
  </Frame>
);

const MOCKS = {
  agenda: AgendaMock,
  odontograma: OdontogramMock,
  ficha: FichaMock,
  consentimiento: ConsentMock,
  receta: RecetaMock,
  rx: RxMock,
};

export const Mock = ({ name }) => {
  const Component = MOCKS[name];
  return Component ? <Component /> : null;
};
